import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * FE#167 — hydration tracker for the SUBSCRIPTION fetch only.
 *
 * The entitlement gates hold while `entitlementStatusSelector` is `unknown`. That is
 * correct but silent: `DBSubscriptionReducer.itemsLoaded` stays false both while the
 * fetch is in flight AND forever after it fails, because the DB-service catch only
 * clears `itemsLoading` and records no error. So "still loading" and "gave up" are
 * indistinguishable, and a customer whose fetch failed would sit against an inert
 * control with no explanation.
 *
 * This slice adds the missing distinction for that ONE fetch:
 *
 *   loading  — a find is in flight (or about to be retried)
 *   loaded   — a find succeeded; entitlement is authoritative
 *   failed   — bounded retries exhausted; show the explicit error + Retry
 *
 * DELIBERATELY NOT in DBServiceReducers. That generic slice backs ~30 collections and
 * the same error-blindness affects all of them, but widening its shape here would be a
 * cross-cutting change riding on a paywall fix. The general problem is filed separately;
 * this stays scoped to subscriptions.
 *
 * `failed` NEVER implies an entitlement. It is a prompt to retry, not a verdict — the
 * whole point of #167 is that we stop inventing an answer when we do not have one.
 */
export type SubscriptionHydrationStatus = 'idle' | 'loading' | 'loaded' | 'failed';

/** Attempts before surfacing the error. Small: this is a fast call on the auth path and
 *  the user is staring at a held control while it runs. */
export const SUBSCRIPTION_HYDRATION_MAX_ATTEMPTS = 3;

export interface SubscriptionHydrationState {
  status: SubscriptionHydrationStatus;
  /** Attempts made in the current cycle; reset when a retry cycle starts. */
  attempts: number;
  /** Diagnostic only — never rendered verbatim to the customer. */
  lastError?: string;
}

const initialState: SubscriptionHydrationState = {
  status: 'idle',
  attempts: 0,
};

const subscriptionHydrationSlice = createSlice({
  name: 'subscriptionHydration',
  initialState,
  reducers: {
    hydrationStarted(state) {
      state.status = 'loading';
      state.attempts = 0;
      state.lastError = undefined;
    },
    hydrationAttemptFailed(state, action: PayloadAction<{ attempt: number; error?: string }>) {
      state.attempts = action.payload.attempt;
      state.lastError = action.payload.error;
      // Stay 'loading' while retries remain — the control keeps waiting rather than
      // flashing an error that the next attempt would immediately clear.
      state.status =
        action.payload.attempt >= SUBSCRIPTION_HYDRATION_MAX_ATTEMPTS ? 'failed' : 'loading';
    },
    hydrationSucceeded(state) {
      state.status = 'loaded';
      state.lastError = undefined;
    },
    /** Manual retry from the error dialog. */
    hydrationRetryRequested(state) {
      state.status = 'loading';
      state.attempts = 0;
      state.lastError = undefined;
    },
    hydrationReset() {
      return { ...initialState };
    },
  },
});

export const {
  hydrationStarted,
  hydrationAttemptFailed,
  hydrationSucceeded,
  hydrationRetryRequested,
  hydrationReset,
} = subscriptionHydrationSlice.actions;

export default subscriptionHydrationSlice.reducer;

/* ----------------------------------------------------------------- selectors */

/** Bounded retries are spent and the fetch never succeeded — the ONLY state that may
 *  surface the "check your connection" dialog. */
export const selectSubscriptionHydrationFailed = (state: {
  SubscriptionHydrationReducer?: SubscriptionHydrationState;
}): boolean => state.SubscriptionHydrationReducer?.status === 'failed';

/** A fetch is in flight (including between retries) — controls hold quietly. */
export const selectSubscriptionHydrationLoading = (state: {
  SubscriptionHydrationReducer?: SubscriptionHydrationState;
}): boolean => {
  const s = state.SubscriptionHydrationReducer?.status;
  return s === 'loading' || s === 'idle';
};

/* -------------------------------------------------------------------- thunk */

/**
 * Fetch subscriptions with bounded retries, recording the outcome on this slice.
 *
 * Wraps `DBSubscriptionThunks.find({})` rather than replacing it: the DB slice remains
 * the source of the subscription data and of `itemsLoaded`; this only records whether
 * the attempt sequence is still running or has given up, which the DB slice cannot say.
 *
 * On exhaustion it resolves (does not throw) with status `failed` — the caller is the
 * auth pipeline and a failed entitlement fetch must not abort login. The gates hold and
 * the dialog explains; nothing guesses an entitlement.
 */
export const hydrateSubscriptionsThunk =
  (opts: { isRetry?: boolean } = {}) =>
  async (dispatch: any): Promise<SubscriptionHydrationStatus> => {
    const { DBSubscriptionThunks } = await import('@common-reducers/DBServiceThunks');
    dispatch(opts.isRetry ? hydrationRetryRequested() : hydrationStarted());

    for (let attempt = 1; attempt <= SUBSCRIPTION_HYDRATION_MAX_ATTEMPTS; attempt++) {
      try {
        // `find` swallows its own errors (the DB-service catch), so a rejection is not
        // the only failure mode — an unsuccessful call can resolve. Treat "resolved but
        // the slice never marked itemsLoaded" as a failed attempt via the thunk result.
        const res: any = await dispatch(DBSubscriptionThunks.find({}));
        if (res?.error === undefined) {
          dispatch(hydrationSucceeded());
          return 'loaded';
        }
        dispatch(hydrationAttemptFailed({ attempt, error: String(res?.error?.message ?? 'find rejected') }));
      } catch (err) {
        dispatch(hydrationAttemptFailed({ attempt, error: err instanceof Error ? err.message : 'unknown' }));
      }
      if (attempt < SUBSCRIPTION_HYDRATION_MAX_ATTEMPTS) {
        // Short linear backoff — the user is looking at a held control, so this is
        // measured in hundreds of ms, not the seconds a background sync could afford.
        await new Promise((r) => setTimeout(r, attempt * 400));
      }
    }
    return 'failed';
  };
